import {
  type PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';
import { notification } from 'antd';
import type { DeepPartial } from 'react-hook-form';

import { userApi } from '@/api/user.api';
import { UserActions, UserPositions } from '@/shared/constants';
import { ROUTES } from '@/shared/routes';
import { getAppNavigate } from '@/shared/services/helpers/navigate-helper';
import type {
  DecodedJWTToken,
  IUserInfoState,
  TModule,
  TUserInfo
} from '@/shared/types/user.types';

import { setIsAuth } from './auth.slice';

export const getUserAccount = createAsyncThunk(
  'userInfo/getUserAccount',
  async () => {
    return userApi.getUserAccount();
  }
);

export const getUserMenu = createAsyncThunk(
  'userInfo/getUserMenu',
  async (_, { dispatch }) => {
    const menu = await userApi.getUserMenu();
    const navigate = getAppNavigate();
    if (!menu) {
      dispatch(setIsAuth(false));
      notification.error({
        title: 'Ошибка получения разделов',
        description: 'Пожалуйста, попробуйте позже'
      });
      navigate(ROUTES.login);
      console.error('Menu is empty or undefined');
      return [];
    } else if (menu.length === 0) {
      notification.warning({
        title: 'Нет доступных разделов',
        description:
          'Пожалуйста, обратитесь к администратору для получения доступа'
      });
      navigate(ROUTES.login);
      return [];
    }
    return menu;
  }
);

const initialUserAccesses: IUserInfoState['userAccesses'] = {
  global: {
    isAdmin: false,
    isUnderwriter: false,
    isLeasingManager: false,
    isLeasingDirector: false,
    isMonitoringManager: false,
    isCarAppraiser: false
  },
  leasing: {
    canMakeDecisions: false
  }
};

const initialState: IUserInfoState = {
  jwtDecodedInfo: null,
  userInfo: null,
  menu: [],
  isSidebarExpanded: false,
  mobileMenuItem: null,
  adminMobileMenuItem: null,
  userAccesses: initialUserAccesses
};

const userInfoSlice = createSlice({
  name: 'userInfoSlice',
  initialState,
  reducers: {
    setJwtDecodedInfo: (state, action: PayloadAction<DecodedJWTToken>) => {
      state.jwtDecodedInfo = action.payload;
      state.userAccesses.leasing.canMakeDecisions =
        action.payload.actions.includes(UserActions.ManualDecisions);
    },
    setSidebarExpanded: (state, action: PayloadAction<boolean>) => {
      state.isSidebarExpanded = action.payload;
    },
    setMobileMenuItem: (state, action: PayloadAction<TModule>) => {
      state.mobileMenuItem = action.payload;
    },
    setAdminMobileMenuItem: (state, action: PayloadAction<string>) => {
      state.adminMobileMenuItem = action.payload;
    },
    resetUserInfo: (state) => {
      state.jwtDecodedInfo = null;
      state.userInfo = null;
      state.menu = [];
      state.mobileMenuItem = null;
      state.adminMobileMenuItem = null;
      state.userAccesses = initialUserAccesses;
    },
    updateUserAccesses: (
      state,
      action: PayloadAction<DeepPartial<IUserInfoState['userAccesses']>>
    ) => {
      state.userAccesses = {
        global: {
          ...state.userAccesses.global,
          ...action.payload.global
        },
        leasing: {
          ...state.userAccesses.leasing,
          ...action.payload.leasing
        }
      };
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(
        getUserAccount.fulfilled,
        (state, action: PayloadAction<TUserInfo>) => {
          state.userInfo = action.payload;
          state.userAccesses.global = {
            ...state.userAccesses.global,
            isAdmin: action.payload.position.code === UserPositions.Admin,
            isUnderwriter:
              action.payload.position.code === UserPositions.Underwriter,
            isLeasingManager:
              action.payload.position.code === UserPositions.LeasingManager,
            isLeasingDirector:
              action.payload.position.code === UserPositions.LeasingDirector,
            isMonitoringManager:
              action.payload.position.code === UserPositions.MonitoringManager,
            isCarAppraiser:
              action.payload.position.code === UserPositions.CarAppraiser
          };
        }
      )
      .addCase(
        getUserMenu.fulfilled,
        (state, action: PayloadAction<TModule[]>) => {
          state.menu =
            action.payload?.sort((a, b) => a.position - b.position) || [];

          const filteredMobileItems = state.menu.filter((item) =>
            ['clients', 'reports'].includes(item.code)
          );
          state.mobileMenuItem = filteredMobileItems[0];
        }
      );
  }
});

export const {
  setJwtDecodedInfo,
  setSidebarExpanded,
  setMobileMenuItem,
  setAdminMobileMenuItem,
  resetUserInfo
} = userInfoSlice.actions;

export default userInfoSlice;
